import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  IconCheck,
  IconAlertTriangle,
  IconCalculator,
  IconRotate,
  IconArrowRight,
  IconClipboardCheck,
  IconAlertCircle,
  IconInfoCircle,
  IconArrowDownLeft,
  IconFlame,
  IconReceipt,
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import {
  createReconciliation,
  approveReconciliation,
  settleAndReconcileCustody,
} from "../../controllers/item_issue.controller";
import toast from "react-hot-toast";

export default function ReconciliationView({
  register,
  eligibleItems,
  shifts,
  users,
  prefilledRow,
  onReconciliationSuccess,
}) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);

  // POS Sale info (informational for balance reconciliation, no connection to main stock)
  const [posSaleNumber, setPosSaleNumber] = useState("");
  const [posSaleQty, setPosSaleQty] = useState("");
  const [posSaleUnit, setPosSaleUnit] = useState(null);
  const [posSaleNotes, setPosSaleNotes] = useState("");

  // Return to store stock
  const [returnQty, setReturnQty] = useState("");
  const [returnUnit, setReturnUnit] = useState(null);
  const [returnNotes, setReturnNotes] = useState("");

  // Non-sale usage / wastage
  const [consumptionQty, setConsumptionQty] = useState("");
  const [consumptionUnit, setConsumptionUnit] = useState(null);
  const [consumptionType, setConsumptionType] = useState("wastage");
  const [consumptionReason, setConsumptionReason] = useState("");

  const [actualCount, setActualCount] = useState("");
  const [selectedShift, setSelectedShift] = useState(null);
  const [reason, setReason] = useState("");
  const [approverNotes, setApproverNotes] = useState("");
  const [autoApprove, setAutoApprove] = useState(true);
  const [closeCustody, setCloseCustody] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Group register by user to find custodians with active stock
  const activeCustodians = useMemo(() => {
    const map = new Map();
    (register || []).forEach((r) => {
      const remaining = parseFloat(r.remaining_base_quantity) || 0;
      const isClosed =
        r.status === "closed" ||
        r.status === "returned" ||
        r.status === "reconciled" ||
        remaining <= 0.0001;
      if (!isClosed && remaining > 0.0001) {
        if (!map.has(r.user_id)) {
          map.set(r.user_id, {
            value: r.user_id,
            label: `${r.employee_name || r.user_id} (${r.department || "Staff"})`,
          });
        }
      }
    });
    return Array.from(map.values());
  }, [register]);

  // Handle prefill from Custody Register
  useEffect(() => {
    if (prefilledRow) {
      const uOpt = activeCustodians.find((c) => c.value === prefilledRow.user_id) || {
        value: prefilledRow.user_id,
        label: `${prefilledRow.employee_name || prefilledRow.user_id} (${prefilledRow.department || "Staff"})`,
      };
      setSelectedUser(uOpt);

      const matchingItem = (eligibleItems || []).find(
        (i) => i.id === prefilledRow.inventory_item_id
      );
      if (matchingItem) {
        setSelectedItem({
          value: matchingItem.id,
          label: `${matchingItem.title} (Remaining: ${prefilledRow.remaining_base_quantity} ${matchingItem.unit})`,
          item: matchingItem,
          custodyRecord: prefilledRow,
        });
      }
    }
  }, [prefilledRow, activeCustodians, eligibleItems]);

  // Products held by selected custodian
  const heldItemOptions = useMemo(() => {
    if (!selectedUser) return [];
    const held = (register || []).filter((r) => {
      const remaining = parseFloat(r.remaining_base_quantity) || 0;
      const isClosed =
        r.status === "closed" ||
        r.status === "returned" ||
        r.status === "reconciled" ||
        remaining <= 0.0001;
      return r.user_id === selectedUser.value && !isClosed && remaining > 0.0001;
    });

    return held.map((r) => {
      const matchingMaster = (eligibleItems || []).find((i) => i.id === r.inventory_item_id);
      return {
        value: r.inventory_item_id,
        label: `${r.item_title} — Custody Rem: ${r.remaining_base_quantity} ${r.unit} (Pending: ${r.pending_sales_base_quantity || 0})`,
        item: matchingMaster || { id: r.inventory_item_id, title: r.item_title, unit: r.unit, alternate_units: [] },
        custodyRecord: r,
      };
    });
  }, [selectedUser, register, eligibleItems]);

  // Units
  const unitOptions = useMemo(() => {
    if (!selectedItem) return [];
    const item = selectedItem.item;
    const list = [
      {
        value: `base_${item.unit}`,
        label: `${item.unit} (Base Stock Unit - 1.0x)`,
        unit_name: item.unit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
      },
    ];

    if (Array.isArray(item.alternate_units)) {
      item.alternate_units.forEach((u) => {
        list.push({
          value: `alt_${u.purchase_unit_id}`,
          label: `${u.unit_name} (${u.conversion_factor} ${item.unit} per ${u.unit_name})`,
          unit_name: u.unit_name,
          purchase_unit_id: u.purchase_unit_id,
          conversion_factor: parseFloat(u.conversion_factor) || 1.0,
        });
      });
    }

    return list;
  }, [selectedItem]);

  const handleItemSelect = (opt) => {
    setSelectedItem(opt);
    if (opt && opt.item) {
      const baseOpt = {
        value: `base_${opt.item.unit}`,
        label: `${opt.item.unit} (Base Stock Unit - 1.0x)`,
        unit_name: opt.item.unit,
        purchase_unit_id: null,
        conversion_factor: 1.0,
      };
      setSelectedUnit(baseOpt);
      setPosSaleUnit(baseOpt);
      setReturnUnit(baseOpt);
      setConsumptionUnit(baseOpt);
    } else {
      setSelectedUnit(null);
      setPosSaleUnit(null);
      setReturnUnit(null);
      setConsumptionUnit(null);
    }
  };

  // Calculations
  const parsedCount = parseFloat(actualCount) || 0;
  const factor = selectedUnit ? selectedUnit.conversion_factor : 1.0;
  const actualBaseQty = parsedCount * factor;

  const custodyRemaining = selectedItem?.custodyRecord
    ? parseFloat(selectedItem.custodyRecord.remaining_base_quantity) || 0
    : 0;
  const pendingSales = selectedItem?.custodyRecord
    ? parseFloat(selectedItem.custodyRecord.pending_sales_base_quantity) || 0
    : 0;

  const posFactor = posSaleUnit ? posSaleUnit.conversion_factor : 1.0;
  const posSaleBaseQty = (parseFloat(posSaleQty) || 0) * posFactor;

  const retFactor = returnUnit ? returnUnit.conversion_factor : 1.0;
  const returnBaseQty = (parseFloat(returnQty) || 0) * retFactor;

  const conFactor = consumptionUnit ? consumptionUnit.conversion_factor : 1.0;
  const consumeBaseQty = (parseFloat(consumptionQty) || 0) * conFactor;

  const expectedPhysicalCount = Math.max(
    0,
    custodyRemaining - posSaleBaseQty - returnBaseQty - consumeBaseQty - pendingSales
  );

  const difference = actualBaseQty - expectedPhysicalCount;
  const hasDiscrepancy = Math.abs(difference) > 0.0001;
  const isShortage = difference < -0.0001;
  const isSurplus = difference > 0.0001;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedUser) {
      toast.error("Please select a custodian.");
      return;
    }
    if (!selectedItem) {
      toast.error("Please select a product.");
      return;
    }
    if (actualCount === "" || parsedCount < 0) {
      toast.error("Please enter a valid actual physical count.");
      return;
    }
    if (posSaleBaseQty > custodyRemaining + 0.0001) {
      toast.error(`POS sale quantity exceeds custody balance.`);
      return;
    }
    if (returnBaseQty > Math.max(0, custodyRemaining - posSaleBaseQty) + 0.0001) {
      toast.error(`Return quantity exceeds remaining custody balance.`);
      return;
    }
    if (consumeBaseQty > Math.max(0, custodyRemaining - posSaleBaseQty - returnBaseQty) + 0.0001) {
      toast.error(`Wastage quantity exceeds remaining custody balance.`);
      return;
    }
    if (hasDiscrepancy && !reason.trim()) {
      toast.error("A reason is mandatory when physical count differs from expected balance.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        user_id: selectedUser.value,
        employee_name: selectedItem.custodyRecord?.employee_name || selectedUser.label,
        department: selectedItem.custodyRecord?.department || null,
        inventory_item_id: selectedItem.value,
        issue_id: selectedItem.custodyRecord?.issue_id || selectedItem.custodyRecord?.id || null,
        pos_sale_number: posSaleNumber.trim() || null,
        pos_sale_reference: posSaleNumber.trim() || null,
        pos_sale_quantity: parseFloat(posSaleQty) || 0,
        pos_sale_purchase_unit_id: posSaleUnit?.purchase_unit_id || null,
        pos_sale_unit_name: posSaleUnit?.unit_name || selectedItem.item.unit,
        pos_sale_notes: posSaleNotes || null,
        return_quantity: parseFloat(returnQty) || 0,
        return_purchase_unit_id: returnUnit?.purchase_unit_id || null,
        return_unit_name: returnUnit?.unit_name || selectedItem.item.unit,
        return_notes: returnNotes || null,
        consumption_quantity: parseFloat(consumptionQty) || 0,
        consumption_purchase_unit_id: consumptionUnit?.purchase_unit_id || null,
        consumption_unit_name: consumptionUnit?.unit_name || selectedItem.item.unit,
        consumption_type: consumptionType || "wastage",
        consumption_reason: consumptionReason || "Non-sale usage / wastage",
        actual_physical_quantity: parsedCount,
        actual_purchase_unit_id: selectedUnit?.purchase_unit_id || null,
        actual_unit_name: selectedUnit?.unit_name || selectedItem.item.unit,
        shift_id: selectedShift ? selectedShift.value : null,
        reconciliation_reason: reason || "Physical count verified matching expected balance",
        close_custody: closeCustody || parsedCount <= 0.0001,
      };

      const res = await settleAndReconcileCustody(payload);
      if (res.data?.success) {
        toast.success(res.data.message || `Audit completed! (Recon #${res.data.reconciliation_no})`);
        setPosSaleNumber("");
        setPosSaleQty("");
        setPosSaleNotes("");
        setReturnQty("");
        setReturnNotes("");
        setConsumptionQty("");
        setConsumptionReason("");
        setActualCount("");
        setReason("");
        setApproverNotes("");
        if (onReconciliationSuccess) onReconciliationSuccess();
      } else {
        toast.error(res.data?.message || "Failed to submit audit.");
      }
    } catch (err) {
      console.error("Reconciliation error:", err);
      toast.error(err.response?.data?.message || err.message || "Failed to submit audit.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-base-100 p-6 rounded-2xl border border-base-200 shadow-sm max-w-4xl mx-auto">
      <div className="border-b border-base-200 pb-4 mb-6">
        <h2 className="text-xl font-bold text-base-content flex items-center gap-2">
          <IconClipboardCheck size={22} stroke={iconStroke} className="text-secondary" />
          Physical Stock Audit & Custody Reconciliation
        </h2>
        <p className="text-xs text-base-content/60 mt-1">
          Perform end-of-shift or spot audits. Verify physical custody against expected balances, detect shortages or surpluses, and record authorized adjustments.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Custodian & Product */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Audited Custodian / Staff <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={activeCustodians}
              value={selectedUser}
              onChange={(opt) => {
                setSelectedUser(opt);
                setSelectedItem(null);
                setSelectedUnit(null);
              }}
              placeholder="Select staff with active custody..."
              isClearable
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Product in Custody <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={heldItemOptions}
              value={selectedItem}
              onChange={handleItemSelect}
              isDisabled={!selectedUser}
              placeholder={selectedUser ? "Select item..." : "Choose custodian first"}
            />
          </div>
        </div>

        {/* Expected Count Breakdown Card */}
        {selectedItem && (
          <div className="bg-base-200/50 p-4 rounded-xl border border-base-200">
            <span className="text-xs font-bold text-base-content/70 uppercase tracking-wider block mb-2">
              System Ledger Balance for {selectedItem.item.title}
            </span>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-xs">
              <div className="bg-base-100 p-2.5 rounded-lg border border-base-200">
                <span className="text-base-content/60 block text-[10px] uppercase font-bold">Total Custody</span>
                <span className="font-bold text-sm text-primary font-mono">
                  {custodyRemaining.toLocaleString()} {selectedItem.item.unit}
                </span>
              </div>
              <div className="bg-base-100 p-2.5 rounded-lg border border-base-200">
                <span className="text-info block text-[10px] uppercase font-bold">(-) POS Sales</span>
                <span className="font-bold text-sm text-info font-mono">
                  {posSaleBaseQty.toFixed(2)} {selectedItem.item.unit}
                </span>
              </div>
              <div className="bg-base-100 p-2.5 rounded-lg border border-base-200">
                <span className="text-success block text-[10px] uppercase font-bold">(-) Return Store</span>
                <span className="font-bold text-sm text-success font-mono">
                  {returnBaseQty.toFixed(2)} {selectedItem.item.unit}
                </span>
              </div>
              <div className="bg-base-100 p-2.5 rounded-lg border border-base-200">
                <span className="text-warning block text-[10px] uppercase font-bold">(-) Wastage</span>
                <span className="font-bold text-sm text-warning font-mono">
                  {consumeBaseQty.toFixed(2)} {selectedItem.item.unit}
                </span>
              </div>
              <div className="bg-primary/10 p-2.5 rounded-lg border border-primary/20 col-span-2 sm:col-span-1">
                <span className="text-primary block text-[10px] uppercase font-bold">(=) Expected Count</span>
                <span className="font-bold text-sm text-primary font-mono">
                  {expectedPhysicalCount.toLocaleString()} {selectedItem.item.unit}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Optional POS Sale, Return & Non-Sale Wastage during Audit */}
        {selectedItem && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-base-200/30 p-4 rounded-xl border border-base-200">
            {/* 1. POS Sale */}
            <div className="space-y-2 bg-base-100 p-3.5 rounded-xl border border-base-200 shadow-2xs">
              <div className="flex items-center gap-1.5 text-info font-bold text-xs uppercase tracking-wide">
                <IconReceipt size={16} stroke={iconStroke} />
                POS Sales Record
              </div>
              <div>
                <label className="label py-0.5">
                  <span className="label-text text-[11px] text-base-content/70">Bill / POS Sale #</span>
                </label>
                <input
                  type="text"
                  className="input input-xs input-bordered w-full text-xs font-mono"
                  placeholder="e.g. INV-1002, POS-042"
                  value={posSaleNumber}
                  onChange={(e) => setPosSaleNumber(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <label className="label py-0.5">
                    <span className="label-text text-[11px] text-base-content/70">Sold Qty</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    className="input input-xs input-bordered w-full text-xs font-bold"
                    placeholder="0.00"
                    value={posSaleQty}
                    onChange={(e) => setPosSaleQty(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label py-0.5">
                    <span className="label-text text-[11px] text-base-content/70">Unit</span>
                  </label>
                  <Select
                    className="text-xs"
                    options={unitOptions}
                    value={posSaleUnit}
                    onChange={setPosSaleUnit}
                    isSearchable={false}
                  />
                </div>
              </div>
              {posSaleBaseQty > 0 && (
                <span className="text-[10px] text-info block font-mono font-semibold">
                  = {posSaleBaseQty.toFixed(2)} {selectedItem.item.unit} recorded as sales
                </span>
              )}
              <div>
                <input
                  type="text"
                  className="input input-xs input-bordered w-full text-[11px]"
                  placeholder="Sale notes (Optional)"
                  value={posSaleNotes}
                  onChange={(e) => setPosSaleNotes(e.target.value)}
                />
              </div>
            </div>

            {/* 2. Return */}
            <div className="space-y-2 bg-base-100 p-3.5 rounded-xl border border-base-200 shadow-2xs">
              <div className="flex items-center gap-1.5 text-success font-bold text-xs uppercase tracking-wide">
                <IconArrowDownLeft size={16} stroke={iconStroke} />
                Return to Store (Optional)
              </div>
              <div>
                <label className="label py-0.5">
                  <span className="label-text text-[11px] text-base-content/70">Return Notes</span>
                </label>
                <input
                  type="text"
                  className="input input-xs input-bordered w-full text-xs"
                  placeholder="e.g. Unopened items back to store"
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <label className="label py-0.5">
                    <span className="label-text text-[11px] text-base-content/70">Return Qty</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    className="input input-xs input-bordered w-full text-xs font-bold"
                    placeholder="0.00"
                    value={returnQty}
                    onChange={(e) => setReturnQty(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label py-0.5">
                    <span className="label-text text-[11px] text-base-content/70">Unit</span>
                  </label>
                  <Select
                    className="text-xs"
                    options={unitOptions}
                    value={returnUnit}
                    onChange={setReturnUnit}
                    isSearchable={false}
                  />
                </div>
              </div>
              {returnBaseQty > 0 && (
                <span className="text-[10px] text-success block font-mono font-semibold">
                  = {returnBaseQty.toFixed(2)} {selectedItem.item.unit} to store
                </span>
              )}
            </div>

            {/* 3. Non-Sale Usage */}
            <div className="space-y-2 bg-base-100 p-3.5 rounded-xl border border-base-200 shadow-2xs">
              <div className="flex items-center gap-1.5 text-warning font-bold text-xs uppercase tracking-wide">
                <IconFlame size={16} stroke={iconStroke} />
                Non-Sale Usage / Wastage
              </div>
              <div>
                <label className="label py-0.5">
                  <span className="label-text text-[11px] text-base-content/70">Usage Reason</span>
                </label>
                <input
                  type="text"
                  className="input input-xs input-bordered w-full text-xs"
                  placeholder="e.g. Broken, Tasting, Expired"
                  value={consumptionReason}
                  onChange={(e) => setConsumptionReason(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <label className="label py-0.5">
                    <span className="label-text text-[11px] text-base-content/70">Wastage Qty</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    className="input input-xs input-bordered w-full text-xs font-bold"
                    placeholder="0.00"
                    value={consumptionQty}
                    onChange={(e) => setConsumptionQty(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label py-0.5">
                    <span className="label-text text-[11px] text-base-content/70">Unit</span>
                  </label>
                  <Select
                    className="text-xs"
                    options={unitOptions}
                    value={consumptionUnit}
                    onChange={setConsumptionUnit}
                    isSearchable={false}
                  />
                </div>
              </div>
              {consumeBaseQty > 0 && (
                <span className="text-[10px] text-warning block font-mono font-semibold">
                  = {consumeBaseQty.toFixed(2)} {selectedItem.item.unit} wastage
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actual Count & Unit */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Count Unit <span className="text-error">*</span>
            </label>
            <Select
              className="text-sm react-select-container"
              classNamePrefix="react-select"
              options={unitOptions}
              value={selectedUnit}
              onChange={setSelectedUnit}
              isDisabled={!selectedItem}
              placeholder={selectedItem ? "Choose unit..." : "Select product first"}
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Actual Physical Count <span className="text-error">*</span>
            </label>
            <input
              type="number"
              step="any"
              min="0"
              required
              className="input input-sm input-bordered w-full text-sm font-bold"
              placeholder="Count verified physically..."
              value={actualCount}
              onChange={(e) => setActualCount(e.target.value)}
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Shift
            </label>
            <select
              className="select select-sm select-bordered w-full text-sm"
              value={selectedShift ? selectedShift.value : ""}
              onChange={(e) => {
                const s = shifts?.find((sh) => String(sh.id) === e.target.value);
                setSelectedShift(s ? { value: s.id, label: s.name } : null);
              }}
            >
              <option value="">Current / Audit Shift</option>
              {shifts?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Discrepancy & Variance Analysis Card */}
        {selectedItem && selectedUnit && actualCount !== "" && (
          <div
            className={`p-4 rounded-xl border ${
              isShortage
                ? "bg-error/5 border-error/30 text-error"
                : isSurplus
                ? "bg-info/5 border-info/30 text-info"
                : "bg-success/5 border-success/30 text-success"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-sm flex items-center gap-2">
                {isShortage ? (
                  <>
                    <IconAlertCircle size={18} stroke={iconStroke} />
                    Stock Shortage Detected
                  </>
                ) : isSurplus ? (
                  <>
                    <IconInfoCircle size={18} stroke={iconStroke} />
                    Stock Surplus Detected
                  </>
                ) : (
                  <>
                    <IconCheck size={18} stroke={iconStroke} />
                    Audit Reconciled — Exact Match
                  </>
                )}
              </span>
              <span className="badge badge-sm font-mono font-bold">
                Variance: {difference > 0 ? `+${difference.toFixed(2)}` : difference.toFixed(2)}{" "}
                {selectedItem.item.unit}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <span className="text-base-content/60 block">Expected Physical</span>
                <span className="font-bold text-sm">
                  {expectedPhysicalCount.toLocaleString()} {selectedItem.item.unit}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Actual Physical Count</span>
                <span className="font-bold text-sm">
                  {actualBaseQty.toLocaleString()} {selectedItem.item.unit}
                </span>
              </div>
              <div>
                <span className="text-base-content/60 block">Status</span>
                <span className="font-bold text-sm uppercase">
                  {isShortage ? "Shortage" : isSurplus ? "Surplus" : "Balanced"}
                </span>
              </div>
            </div>

            {isShortage && (
              <p className="mt-2 text-xs font-semibold">
                Custodian has {Math.abs(difference).toFixed(2)} {selectedItem.item.unit} missing! Reason and supervisor approval are required to adjust.
              </p>
            )}
            {isSurplus && (
              <p className="mt-2 text-xs font-semibold">
                Physical count is {difference.toFixed(2)} {selectedItem.item.unit} higher than recorded in the system.
              </p>
            )}
          </div>
        )}

        {/* Reason & Approver */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Discrepancy Justification / Reason {hasDiscrepancy && <span className="text-error">*</span>}
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Unaccounted table breakage, offline emergency sale, physical count error"
            />
          </div>

          <div>
            <label className="label text-xs font-semibold uppercase tracking-wider text-base-content/70">
              Audit Notes / Supervisor Remarks
            </label>
            <input
              type="text"
              className="input input-sm input-bordered w-full text-sm"
              value={approverNotes}
              onChange={(e) => setApproverNotes(e.target.value)}
              placeholder="e.g. Verified by Duty Manager on shift end"
            />
          </div>
        </div>

        {/* Direct Approve Toggle */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="autoApproveCheck"
            className="checkbox checkbox-sm checkbox-primary"
            checked={autoApprove}
            onChange={(e) => setAutoApprove(e.target.checked)}
          />
          <label htmlFor="autoApproveCheck" className="text-xs font-medium text-base-content cursor-pointer">
            Approve immediately and apply adjustment movement to custody ledger
          </label>
        </div>

        {/* Finalize & Close Custody Toggle */}
        <div className="bg-primary/5 rounded-xl p-3 border border-primary/20 flex items-start gap-3">
          <input
            type="checkbox"
            id="chk_close_custody_recon"
            className="checkbox checkbox-primary checkbox-sm mt-0.5"
            checked={closeCustody}
            onChange={(e) => setCloseCustody(e.target.checked)}
          />
          <label htmlFor="chk_close_custody_recon" className="cursor-pointer text-xs space-y-0.5">
            <span className="font-semibold text-base-content block">
              Finalize & Close Custody (Remove from Product in Custody & Product in Return)
            </span>
            <span className="text-[11px] text-base-content/60 block">
              When checked (or when actual count is 0), this custody record will be permanently marked as closed and removed from active custody and return options.
            </span>
          </label>
        </div>

        {/* Submit */}
        <div className="flex justify-end pt-4 border-t border-base-200">
          <button
            type="submit"
            disabled={isSubmitting || actualCount === "" || !selectedItem}
            className="btn btn-secondary gap-2 px-8"
          >
            {isSubmitting ? (
              <IconRotate className="animate-spin" size={18} stroke={iconStroke} />
            ) : (
              <IconCheck size={18} stroke={iconStroke} />
            )}
            {autoApprove ? "Submit & Approve Audit" : "Save Audit for Review"}
          </button>
        </div>
      </form>
    </div>
  );
}
